import React from "react";
import Chart from "react-apexcharts";
import CurrencyConverter from "@heartinz/currency_subunit_converter";

const formatTime = (hour) => {
  if (hour === 0) return "12 AM";
  if (hour === 12) return "12 PM";
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
};
const processData = (hoursData) => {
  const sortedHoursData = hoursData.sort((a, b) => {
    const aStart = parseInt(a.hourSlot.split("-")[0]);
    const bStart = parseInt(b.hourSlot.split("-")[0]);
    return aStart - bStart;
  });

  const totalAmountData = [];
  const noOfSalesData = [];

  sortedHoursData.forEach((item) => {
    const [startHour, endHour] = item.hourSlot.split("-").map(Number);

    let timeRange;
    let midpoint;

    if (endHour) {
      midpoint = (startHour + endHour) / 2;
      timeRange = `${formatTime(startHour)} - ${formatTime(endHour)}`;
    } else {
      midpoint = startHour;
      timeRange = formatTime(startHour);
    }

    totalAmountData.push({
      x: midpoint,
      y: CurrencyConverter.toCurrency(item.totalAmount || 0, "INR"),
      time_range: timeRange,
    });

    noOfSalesData.push({
      x: midpoint,
      y: item.noOfSales,
      time_range: timeRange,
    });
  });

  return { totalAmountData, noOfSalesData };
};
function WeekSaleAnalytics({ AnalyticsData }) {
  const axisLabelStyle = {
    fontSize: "12px",
    fontWeight: "500",
    // colors: ['#6c757d'],
    fontFamily: "Inter, sans-serif !important ",
  };

  const axisTitleStyle = {
    fontSize: "14px",
    fontWeight: "500",
    colors: ["#636578"],
    fontFamily: "Inter, sans-serif !important",
  };

  const hoursData = AnalyticsData?.saleData || [];

  const { totalAmountData, noOfSalesData } = processData(hoursData);
  let minHour =
    hoursData.length > 0
      ? Math.min(...hoursData.map((d) => Number(d.hourSlot.split("-")[0])))
      : 0;
  let maxHour =
    hoursData.length > 0
      ? Math.max(...hoursData.map((d) => Number(d.hourSlot.split("-")[1])))
      : 24;

  const seriesData = hoursData.map((item) => {
    const [startHour, endHour] = item.hourSlot.split("-").map(Number);
    const midpoint = (startHour + endHour) / 2;
    return {
      x: midpoint,
      y: CurrencyConverter.toCurrency(item.totalAmount || 0, "INR"),
      slot: item.hourSlot,
    };
  });

  seriesData.sort((a, b) => a.x - b.x);

  const chartOptions = {
    chart: {
      foreColor: "#9ba7b2",
      height: 360,
      type: "line",
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
      dropShadow: {
        enabled: true,
        top: 3,
        left: 14,
        blur: 4,
        opacity: 0.1,
      },
    },
    stroke: {
      width: 5,
      curve: "smooth",
    },
    legend: {
      show: false,
    },
    colors: ["#303f9f", "#9da3cbff"],
    xaxis: {
      type: "numeric",
      tickAmount: 24,
      min: minHour,
      max: maxHour,
      title: {
        text: "Time",
        style: axisTitleStyle,
      },
      labels: {
        formatter: (value) => {
          return formatTime(Math.floor(value));
        },
        style: axisLabelStyle,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      crosshairs: {
        show: true,
        width: 1,
        fill: {
          type: "solid",
          color: "#B1B9C4",
          gradientToColors: ["#EBEFF9"],
          opacity: 0.1,
        },
        stroke: {
          color: "#B1B9C4",
          width: 1,
          dashArray: 0,
        },
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        gradientToColors: ["#303f9f"],
        shadeIntensity: 1,
        type: "horizontal",
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 100, 100, 100],
      },
    },
    yaxis: [
      {
        min: 0,
        title: {
          text: "Total Amount",
          style: axisTitleStyle,
        },
        labels: {
          formatter: function (value) {
            return Math.round(value || 0, "INR");
          },
          style: { ...axisLabelStyle, colors: ["#888888"] },
        },
      },
      {
        min: 0,
        opposite: true,
        title: { text: "No of Sales", style: axisTitleStyle },
        labels: {
          formatter: function (value) {
            if (Number.isInteger(value)) {
              return value;
            }
            return "";
          },
          style: { ...axisLabelStyle, colors: ["#888"] },
        },
      },
    ],
    tooltip: {
      enabled: true,
      theme: "light",
      x: {
        formatter: (value) => {
          const originalSlot = hoursData.find((d) => {
            const [startHour, endHour] = d.hourSlot.split("-").map(Number);
            return (startHour + endHour) / 2 === value;
          });
          if (originalSlot) {
            const [start, end] = originalSlot.hourSlot.split("-").map(Number);
            return `${formatTime(start)} - ${formatTime(end)}`;
          }
          return "";
        },
      },
      y: [
        {
          formatter: function (value) {
            return `₹${value || 0}`;
          },
          title: {
            formatter: () => "Sale Amount:",
          },
        },
        {
          formatter: (value) => value,
          title: { formatter: () => "No of Sales:" },
        },
      ],
      marker: {
        show: true,
      },
    },
    markers: {
      size: 4,
      colors: ["#303f9f", "#9da3cbff"],
      strokeColors: "#fff",
      strokeWidth: 2,
      fillOpacity: 1,
      shape: "circle",
      radius: 2,
      hover: {
        size: 7,
      },
    },
    grid: {
      row: {
        colors: ["transparent"],
        opacity: 0.5,
      },
    },
  };

  const chartSeries = [
    {
      name: "No of Sales",
      type: "line",
      data: noOfSalesData,
      color: "#9da3cbff",
    },
    {
      name: "Sale Amount",
      type: "line",
      data: totalAmountData,
      color: "#303f9f",
    },
    
  ];

  const legendItems = [
      { label: "No of Sales", color: "#9da3cbff" },
    { label: "Sale Amount", color: "#303f9f" },
  
  ];

  return (
    <>
      <div className="card-body p-0">
        <div className="row row-bordered g-0 h-100">
          <>
            <div className="col-md-12 col-12 p-3">             
                <>
                  <div className="d-flex justify-content-end gap-2">
                    {legendItems.map((item) => (
                      <div
                        className="d-flex justify-content-center align-items-center font-13 gap-1 me-2"
                        key={item.label}
                      >
                        <span
                          style={{
                            width: 12,
                            height: 12,
                            backgroundColor: item.color,
                            borderRadius: "12px",
                          }}
                        />
                        {item.label}
                      </div>
                    ))}
                  </div>
                  <Chart
                    options={chartOptions}
                    series={chartSeries}
                    type="line"
                    height={300}
                  />
                </>              
            </div>
          </>
        </div>
      </div>
    </>
  );
}

export default WeekSaleAnalytics;
